// The one place http.ts gets its base URL and its access token from. Kept as a separate module,
// rather than importing the MSAL auth hook directly into http.ts, so the API layer (the Resource
// Access analog per TS-*'s layer mapping) never depends on the auth layer's implementation - only
// on this narrow seam. AuthProvider calls configureApi() once, at startup, after MSAL initializes.
export interface ApiConfig {
  readonly baseUrl: string;
  /** Returns a bearer token for the next request, or null when there is no signed-in session. */
  getAccessToken: () => Promise<string | null>;
  /** Invoked when a request comes back 401; typically triggers a fresh sign-in. */
  onUnauthenticated: () => void;
}

// Empty string means "same origin" - every client call passes a path already rooted at /api or
// /hubs, and the Vite dev proxy (vite.config.ts) forwards both to the configured API host locally.
const defaultConfig: ApiConfig = {
  baseUrl: (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "",
  getAccessToken: () => Promise.resolve(null),
  onUnauthenticated: () => {
    // No-op until AuthProvider installs a real handler.
  },
};

let current: ApiConfig = defaultConfig;

export function configureApi(config: Partial<ApiConfig>): void {
  current = { ...current, ...config };
}

export function getApiConfig(): ApiConfig {
  return current;
}

/** Test-only: restores the default (unauthenticated, same-origin) configuration. */
export function resetApiConfig(): void {
  current = defaultConfig;
}
